import { Checkbox, Select } from "antd";
import moment from "moment";
import { useAppSelector } from "../../../app/store";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import UserImage from "../../../assets/images/avatar.svg";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import {
  useGetLeaveAdjustmentDetailsQuery,
  useGetLeaveTypeListQuery,
} from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import { ModalComponentProps } from "../../../types";
import { selectSearchOption } from "../../../utils/utils";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function ViewAdjustmentModal({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<number | undefined>) {
  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    data: detailsData,
    isLoading,
    isError,
    error,
  } = useGetLeaveAdjustmentDetailsQuery(Number(data), {
    skip: !data,
  });
  const {
    data: leaveTypeListData,
    isLoading: isLeaveTypeListLoading,
    isError: isLeaveError,
    error: leaveError,
  } = useGetLeaveTypeListQuery({
    params: {
      limit: 0,
      skip: 0,
      is_published: 1,
    },
  });

  const details = detailsData?.data?.leave_adjustment_policy;
  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
  } = useGetDepartmentsQuery(
    {
      params: { limit: 0, skip: 0, is_published: 1 },
    },
    {
      skip: details?.applicable_for !== "department",
    }
  );
  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: {
      status: "active",
      manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
      skip: 0,
      limit: 0,
    },
  });
  useError(isDepartmentError, departmentError, "Get department fail");
  useError(isError, error, "Get adjustment fail");
  useError(isLeaveError, leaveError, "Failed fetching leave type");
  useError(isEmployeeError, employeeError, "Get employee fail");

  const adjustmentPolicy = leaveTypeListData?.data?.leave_types?.map(
    (el: any) => {
      const findData = details?.leave_types?.find(
        (item) => item?.leave_type_id === el?.id
      );

      return {
        sequence: findData?.sequence,
        leave_type_id: el?.id,
        title: el?.title,
      };
    }
  );

  const departmentItem = departmentData?.data?.departments?.filter((el) => {
    const id = el?.id;
    return (
      id != null && details?.targets?.find((item) => item?.target_id === id)
    );
  });

  const filteredEmployees = employeeData?.data?.employees?.filter((el) => {
    const id = el?.id;
    return (
      id != null && details?.targets?.find((item) => item?.target_id === id)
    );
  });

  return (
    <CustomModal
      title="Attendance Adjustment"
      {...rest}
      handleClose={handleClose}
      width={556}
      footer={null}
    >
      {isLoading || isLeaveTypeListLoading ? (
        <Loader />
      ) : (
        <div className="space-y-5">
          <div>
            <h6 className="text-body text-lg font-medium">Effective From</h6>
            <p className="text-body text-sm font-normal">
              {moment(details?.effective_from, dbDateFormat).format(dateFormat)}
            </p>
          </div>
          <div>
            <h6 className="text-body text-lg font-medium">
              Adjustment Sequence
            </h6>

            <div>
              {adjustmentPolicy?.map((field: any) => (
                <div key={field?.leave_type_id} className="px-2 py-1.5">
                  <div className="flex justify-between items-center">
                    <label>
                      <div className="text-body-text text-sm font-normal">
                        {field?.title}
                      </div>
                    </label>

                    <Select
                      size="small"
                      showSearch
                      filterOption={selectSearchOption}
                      value={field?.sequence}
                      className="min-w-[80px]"
                      suffixIcon={<SelectDownIcon />}
                      placeholder="Select"
                      disabled
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {details?.applicable_for === "department" && (
            <div>
              <h6 className="text-body text-lg font-medium mb-2">Department</h6>
              <div className="text-body text-sm font-normal">
                <div className="grid grid-cols-3  gap-x-1 gap-y-2 p-2.5">
                  {departmentItem?.map((item) => (
                    <Checkbox
                      key={item?.id}
                      className="text-header !text-sm font-medium cursor-default"
                      checked
                      value={item?.id}
                      onChange={(e) => e.preventDefault()}
                    >
                      {item?.name}
                    </Checkbox>
                  ))}
                </div>
              </div>
            </div>
          )}

          {details?.applicable_for === "employee" && (
            <div>
              <h6 className="text-body text-lg font-medium mb-2">Employee</h6>
              <div className=" space-y-2.5">
                {filteredEmployees?.map((el) => (
                  <div
                    className="p-3 flex justify-between items-center rounded outline outline-1 outline-offset-[-1px] outline-light"
                    key={el?.id}
                  >
                    <div className="flex-1 flex items-center gap-3">
                      <img
                        className="w-10 h-10 rounded-full"
                        src={el?.member?.profile?.pro_pic || UserImage}
                        alt=""
                      />
                      <div>
                        <h3 className="text-body text-sm font-medium">
                          {el?.member?.profile?.name}
                        </h3>
                        <h6 className="text-display-body text-xs font-medium">
                          {el?.role?.name}
                        </h6>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </CustomModal>
  );
}
